import React, { useState, useRef, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { Edit } from '@material-ui/icons'

// Context
import { useUser } from 'context/user/user.context'

// Components
import { TextArea, Button, Input } from 'components'

import { translate } from 'locales/index'
import { alertSuccess, alertDanger } from 'utils/toast'

// Styles
import defaultImg from 'assets/images/avatar.jpg'
import aiIcon from 'assets/icons/mage_stars-c.svg'
import * as S from './styles'

// Services
import {
  updateProfileBio,
  findUserData,
  uploadProfilePhoto,
  generateBioWithAI,
  getProfileBio,
} from './services'

const Photo: React.FunctionComponent<{ setLoading?: any }> = ({
  setLoading,
}) => {
  const { userData, setUserData } = useUser()
  const [bio, setBio] = useState('')
  const [loadingAI, setLoadingAI] = useState(false)
  const [isAIGenerated, setIsAIGenerated] = useState(false)
  const [professionalInfo, setProfessionalInfo] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      /* eslint-disable */
      const maxSize = 4 * 2048 * 2048

      if (!acceptedFiles[0]) return

      const acceptFiles = [
        'image/jpeg',
        'image/pjpeg',
        'image/png',
        'image/gif',
      ]

      if (acceptedFiles[0].size > maxSize) {
        return alertDanger('A imagem não pode ser maior que 16Mb!')
      }

      if (!acceptFiles.includes(acceptedFiles[0].type)) {
        return alertDanger(
          'Tipo do arquivo inválido, só são aceitos imagens do tipo JPG, JPEG, PNG, GIF.'
        )
      }

      if (setLoading) setLoading(true)

      const doctorId = userData?.id

      try {
        await uploadProfilePhoto(acceptedFiles[0], doctorId)
        if (setLoading) setLoading(false)
        findUserData(setUserData, setLoading)
        alertSuccess('Foto alterada com sucesso!')
      } catch (err: any) {
        if (setLoading) setLoading(false)
        alertDanger('Erro ao alterar a foto, tente novamente!')
      }
    },
  })

  const handleEditPhotoClick = () => {
    fileInputRef.current?.click()
  }

  const handleImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setBio(newValue)
    if (isAIGenerated) {
      setIsAIGenerated(false)
    }
  }

  const handleSaveBio = async () => {
    if (setLoading) setLoading(true)
    try {
      await updateProfileBio(bio, userData.id, isAIGenerated)
      setUserData({
        ...userData,
        profile: {
          ...userData.profile,
          bio,
        },
      })
      setIsAIGenerated(false)
      alertSuccess('Bio atualizada com sucesso!')
    } catch (error: any) {
      alertDanger(error?.response?.data?.message || 'Erro ao atualizar bio')
    } finally {
      if (setLoading) setLoading(false)
    }
  }

  const handleAISuggestions = async () => {
    setLoadingAI(true)
    try {
      const generatedBio = await generateBioWithAI(professionalInfo)
      setBio(generatedBio)
      setIsAIGenerated(true)
      alertSuccess('Bio gerada com sucesso pela IA!')
    } catch (error: any) {
      alertDanger(
        error?.message || 'Erro ao gerar sugestão de bio. Tente novamente.'
      )
    } finally {
      setLoadingAI(false)
    }
  }

  useEffect(() => {
    const loadBio = async () => {
      if (userData?.id) {
        try {
          const existingBio = await getProfileBio(userData.id)
          if (existingBio) {
            setBio(existingBio)
          }
        } catch (error) {
          console.error('Erro ao carregar bio:', error)
        }
      }
    }

    loadBio()
  }, [userData?.id])

  const currentPhoto = userData.profile.photo || defaultImg

  return (
    <S.PhotoProfile>
      <div className="photo-section">
        <div className="section-header">
          <h3>{translate('profile.photo')}</h3>
        </div>
        <div className="content-photo" {...getRootProps()}>
          <input
            {...getInputProps()}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <div className="photo-content-wrapper">
            <img
              src={currentPhoto}
              alt={userData.name}
              onClick={handleImageClick}
              style={{ cursor: 'pointer' }}
            />
            <button
              className="edit-photo-button"
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleEditPhotoClick()
              }}
            >
              <Edit style={{ fontSize: 16, color: '#fff' }} />
            </button>
          </div>
        </div>
      </div>

      <div className="bio-section">
        <div className="section-header">
          <h3>{translate('profile.bio') || 'Bio'}</h3>
        </div>

        <S.ProfessionalInfoContainer>
          <S.ProfessionalInfoWrapper>
            <h4>{translate('profile.professionalInfoLabel')}</h4>
            <Input
              name="professionalInfo"
              placeholder="Insira aqui"
              value={professionalInfo}
              type="text"
              onChange={(e) => setProfessionalInfo(e.target.value)}
            />
          </S.ProfessionalInfoWrapper>
          <S.AISuggestionsButton
            onClick={handleAISuggestions}
            type="button"
            disabled={loadingAI || !professionalInfo.trim()}
          >
            <img src={aiIcon} alt="IA" />
            {loadingAI
              ? translate('profile.generating')
              : translate('profile.aiSuggestions')}
          </S.AISuggestionsButton>
        </S.ProfessionalInfoContainer>

        <S.BioTextArea>
          <TextArea
            name="bio"
            placeholder="Escreva aqui..."
            value={bio}
            onChange={handleBioChange}
            rows={3}
          />
        </S.BioTextArea>
        <div className="bio-actions">
          <Button
            type="button"
            color="primary"
            size="small"
            onClick={handleSaveBio}
            disabled={bio === (userData?.profile?.bio || '')}
          >
            {translate('profile.save') || 'Salvar'}
          </Button>
        </div>
      </div>
    </S.PhotoProfile>
  )
}

export default Photo
